/**
 * Grouping the contributor's received feedback by reason chip (#744).
 *
 * `GET /feedback/received` returns feedback grouped by the entry it was left on
 * (`ContributorFeedbackGroup`, one per installName across a plugin and the skills
 * synthesized from its `skills[]`). That is the right shape for the WIRE — it is what
 * keeps a comment on one skill from being misread as a comment on the plugin — but it
 * is the wrong shape for READING: a contributor with a plugin and four skills gets five
 * chronological lists and has to spot the recurring theme themselves.
 *
 * So this re-pivots the same items onto the chip axis: one count-sorted list across the
 * whole family, biggest pattern first. The no-merge guarantee is preserved at the ROW
 * level rather than the group level — every item carries the `installName` it was left
 * on, so the UI can still attribute it, and no two items are ever fused into one row.
 *
 * Pure and side-effect free, so the ordering rules below are testable without rendering.
 */

import type {
  ContributorFeedbackGroup,
  ContributorFeedbackItem,
  ReasonCode,
} from "@skills-marketplace/contracts/contract-8-feedback";
import { reasonLabel } from "./feedback";

/** What the parent plugin's own row reads, instead of echoing its installName. */
export const PARENT_ENTRY_LABEL = "the plugin itself";

/**
 * The separator deriveSkillInstallName puts between a parent's key and a skill slug.
 * Mirrored here for READING only — lib/shared/skill-install-name.ts stays the single
 * implementation that CONSTRUCTS these keys.
 */
const SKILL_KEY_SEPARATOR = "--skills--";

/** Heading for the synthetic group holding feedback that carries no chip at all. */
export const NO_REASON_LABEL = "No reason given";

/** One piece of feedback, still tied to the entry it was actually left on. */
export interface ReasonGroupItem {
  /** The catalog entry voted on — the plugin, or one of its synthesized skills. */
  installName: string;
  item: ContributorFeedbackItem;
}

/** Every piece of feedback that carries one particular chip. */
export interface ReasonGroup {
  /**
   * The reason code, or `null` for the synthetic no-chip group.
   *
   * Grouping keys off the CODE, never the label: Contract 8 stores codes precisely so
   * a chip can be reworded without forking one meaning across two strings. Keying on
   * the label instead would split a historical count in two on the next copy change —
   * the bug the prototype's label-keyed `groupFeedbackByChip` carries.
   */
  code: ReasonCode | null;
  /** Display text for the heading. Cosmetic, derived from `code`. */
  label: string;
  items: ReasonGroupItem[];
}

/**
 * Re-pivot per-entry feedback onto the reason-chip axis, biggest group first.
 *
 * Three behaviours worth stating, all matching the validated prototype:
 *
 *   - An item with SEVERAL chips (multi-select, per #618) appears in EVERY one of its
 *     chips' groups. That is deliberate duplication, not double-counting: a group
 *     count answers "how many pieces of feedback said this", and one piece of feedback
 *     can genuinely say two things. Consequence to expect: group counts sum to more
 *     than the number of entries.
 *   - An item with NO chips lands in the synthetic `null` group rather than
 *     disappearing. This is EVERY chip-less vote, not just "Other"-only free text:
 *     the reason step is optional and #618 gives Skip equal visual weight to Submit
 *     "so declining to give a reason costs the voter nothing", and the server applies
 *     no reason filter (listFeedbackForEntry returns every vote row on the entry).
 *     Expect this bucket to be the LARGEST one on most families.
 *   - Which is why it sorts LAST regardless of size. It is the absence of a pattern,
 *     so letting it win on raw count would put a wall of "Useful, no comment" rows
 *     above the one group with something to read — reintroducing, at the group level,
 *     the "read everything to find the theme" problem #744 exists to fix.
 *   - Ties break alphabetically by label, not by insertion order. Insertion order here
 *     is chip-discovery order across the entry list, which is an artifact of how the
 *     server happened to page — meaningless to a contributor, and unstable between two
 *     reads of the same data.
 *
 * A chip with zero items never appears: groups are built from the items present, so
 * "omit empty chips" is structural rather than a filter that could be forgotten.
 *
 * Within a group, items are newest first. The server sorts per ENTRY
 * (listFeedbackForEntry), but a chip group spans entries, so without re-sorting here a
 * group's rendered dates zigzag by entry-discovery order.
 */
export function groupByReason(groups: ContributorFeedbackGroup[]): ReasonGroup[] {
  const byCode = new Map<ReasonCode | null, ReasonGroupItem[]>();

  for (const group of groups) {
    for (const item of group.items) {
      // No de-duplication here on purpose: uniqueness is enforced where it is actually
      // checkable, on the write path (validateReasonCodes in feedback-api rejects a
      // body with duplicate codes outright), so no stored row can carry one. A client
      // -side guard would have implied otherwise while protecting an unreachable state.
      const codes: (ReasonCode | null)[] =
        item.reasonCodes.length > 0 ? item.reasonCodes : [null];

      for (const code of codes) {
        const bucket = byCode.get(code);
        const entry = { installName: group.installName, item };
        if (bucket) bucket.push(entry);
        else byCode.set(code, [entry]);
      }
    }
  }

  return Array.from(byCode, ([code, items]) => ({
    code,
    label: code === null ? NO_REASON_LABEL : reasonLabel(code),
    // votedAt is ISO-8601 UTC, so lexicographic ordering IS chronological. A row with
    // an unreadable date (toVote yields "") sorts to the end rather than throwing.
    items: [...items].sort((a, b) => b.item.votedAt.localeCompare(a.item.votedAt)),
  })).sort((a, b) => {
    // The no-chip bucket never outranks a real pattern, however large it grows.
    if ((a.code === null) !== (b.code === null)) return a.code === null ? 1 : -1;
    return b.items.length - a.items.length || a.label.localeCompare(b.label);
  });
}

/**
 * A short, readable name for the entry a piece of feedback was left on.
 *
 * Needed because the chip axis spans a whole family: a group can hold a comment on the
 * plugin next to a comment on one of its skills, so each row has to say which. The raw
 * installName is the wrong thing to show many times over — a synthesized skill's key is
 * the parent's key plus `--skills--<slug>` (deriveSkillInstallName in
 * lib/shared/skill-install-name.ts), so an unshortened list repeats the same long prefix
 * on every row and buries the part that differs.
 *
 * Three cases, and the reasoning behind each:
 *
 *   - The PARENT gets prose rather than its own name. This is the one row that is
 *     categorically different — feedback on the plugin is not feedback on one of its
 *     skills — and echoing `observability` next to three skill slugs makes it read as
 *     just another sibling.
 *   - A CHILD gets its slug verbatim, deliberately not title-cased. The slug is a lossy
 *     one-way transform of the skill name (lowercased, non-alphanumerics collapsed to
 *     hyphens), so "ADR Writer" is already gone by the time we see `adr-writer` —
 *     title-casing would render "Adr Writer", a name nobody wrote. Passing the slug
 *     through is honest about being an identifier; inventing capitalisation is not.
 *   - ANYTHING ELSE falls back to the full installName. A child's key need not start
 *     with the parent's: the catalog builder can point a skill at a same-repo standalone
 *     leaf, which familyTally in MySkillsPage guards for the same reason. Chopping such
 *     a key on its last `--` would sometimes read well (`git-publisher`) and sometimes
 *     terribly (`v2`), with no signal about which — so the fallback stays long and
 *     self-announcing instead of silently lossy.
 *
 * Display only. Contract 8 treats installName as an opaque id that only the catalog
 * builder constructs, so this degrades rather than assumes: it never throws, and for
 * any non-empty `installName` it never returns "" — an empty label would leave a row
 * looking like it came from nowhere.
 *
 * An EMPTY `installName` is the one input it cannot label, and it returns "" for it
 * rather than inventing a placeholder. That is unreachable through real data —
 * `deriveInstallName` throws on an empty `manifestName`, so the catalog cannot publish
 * one and the API cannot return one — and a synthetic string here would be dead copy
 * for a state that only a bad fixture can produce. The tests pin this explicitly so the
 * boundary is a decision rather than an oversight.
 *
 * `parentInstallName` is the family root (the installName the expand was requested for).
 */
export function entryLabel(installName: string, parentInstallName: string): string {
  if (installName === parentInstallName) return PARENT_ENTRY_LABEL;

  const prefix = `${parentInstallName}${SKILL_KEY_SEPARATOR}`;
  if (installName.startsWith(prefix)) {
    const slug = installName.slice(prefix.length);
    // An empty slug means a malformed key, not a skill called "". Fall through to the
    // full installName rather than rendering a blank label.
    if (slug.length > 0) return slug;
  }

  return installName;
}
